import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LocationSearch, { LocationSearchSelectedProps } from './LocationSearch';
import DateSearch from './DateSearch';
import GuestSearch from './GuestSearch';
import { generateHotelDetailUrlWithFullInfo, getHotelDetailRequest, getHotelSearchRequest } from '@/urls';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { HotelInfoModel, HotelSearchRequest, RoomSearchInfo } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { BoardType, LocationType } from '@/constants';
import { DATE_ONLY_FORMAT, HOTEL_SEARCHBOX_CONFIG } from '@/config';
import hotelService from '@/services/hotelService';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGetQuery } from '@/hooks/useQuery';
import MealSearch from './MealSearch';
import { useGetSiteSettings } from '@/contexts/SiteSettingContext';

type Props = {
  onSearchHotel: (request: HotelSearchRequest) => void;
  onSearchDetailHotel?: (hotelName: string, hotelCode: string, checkinDate: any, checkoutDate: any, boardType: any, rooms: RoomSearchInfo[]) => void;
  hotelInfo?: HotelInfoModel;
  loading?: boolean;
  height?: string;
};

type DateSearchType = Date | null | undefined;
type SearchRefType = { close: Function; open: Function };
type DateRefType = { close: Function; open: Function; getDates: () => [Date | undefined, Date | undefined] };

export default function SearchBox({ onSearchHotel, hotelInfo, onSearchDetailHotel, loading, height = 'h-60' }: Props) {
  const siteSettings = useGetSiteSettings();
  //   hook
  const { t } = useTranslation();
  const { search } = useLocation();
  const history = useHistory();
  const locationCode = useGetQuery('locationCode');

  // state

  const locationRef = React.useRef<SearchRefType>();
  const [selectedLocation, setSelectedLocation] = React.useState<LocationSearchSelectedProps | null | undefined>();

  const dateRef = React.useRef<DateRefType>();
  const [dates, setDates] = React.useState<{ from: DateSearchType; to: DateSearchType }>({
    from: HOTEL_SEARCHBOX_CONFIG.__defaultStartDate(siteSettings.cutoffDays),
    to: HOTEL_SEARCHBOX_CONFIG.__defaultEndDate(siteSettings.cutoffDays),
  });

  const guestRef = React.useRef<SearchRefType>();
  const [rooms, setRooms] = useState<RoomSearchInfo[]>([HOTEL_SEARCHBOX_CONFIG.defaultRoomValue]);
  const [boardType, setBoardType] = React.useState<number>(HOTEL_SEARCHBOX_CONFIG.defaultMealType);
  React.useEffect(() => {
    if (!hotelInfo) {
      if (locationCode) {
        apiSdk.HotelDestinationService.getDestination({ code: locationCode }).then(res => {
          setSelectedLocation({
            code: res.code || '',
            name: res.name || '',
            type: res.type || LocationType.City,
          });
        });
      }
    }
  }, [locationCode, hotelInfo]);

  React.useEffect(() => {
    if (hotelInfo) {
      let request = getHotelDetailRequest(search);
      if (request) {
        setDates({
          from: moment(request.checkinDate).toDate(),
          to: moment(request.checkoutDate).toDate(),
        });
        setRooms(request.rooms);
        if (request.boardType) {
          setBoardType(request.boardType);
        }
        setSelectedLocation({
          code: hotelInfo.code || '',
          name: hotelInfo.nameEn || '',
          type: LocationType.Hotel,
        });
      }
    } else {
      let request = getHotelSearchRequest(search, true);

      if (request?.checkinDate && request.checkoutDate) {
        setDates({
          from: moment(request.checkinDate).toDate(),
          to: moment(request.checkoutDate).toDate(),
        });
      }
      if (request?.rooms?.length) {
        setRooms(request.rooms);
      }
      if (request?.boardType) {
        setBoardType(request.boardType);
      }
    }
  }, [hotelInfo, search]);

  // function
  const onSearch = () => {
    if (!selectedLocation) {
      locationRef?.current?.open();
      return;
    }
    if (!(dates && dates.from && dates.to)) {
      dateRef.current?.open();
      return;
    }
    if (!rooms.length) {
      guestRef.current?.open();
      return;
    }
    let roomsSearch = rooms.map((r, index) => ({ ...r, roomIndex: index }));
    hotelService.saveRecentSearchInfo(selectedLocation.name, selectedLocation.code, selectedLocation.type);
    if (selectedLocation.type === LocationType.Hotel) {
      if (onSearchDetailHotel) {
        onSearchDetailHotel(selectedLocation.name, selectedLocation.code, dates.from, dates.to, boardType, roomsSearch);
      } else {
        let url = generateHotelDetailUrlWithFullInfo(selectedLocation.name, selectedLocation.code, dates.from, dates.to, boardType, roomsSearch);
        history.push(url);
      }
      return;
    }
    onSearchHotel({
      checkinDate: dates.from as any,
      checkoutDate: dates.to as any,
      locationCode: selectedLocation.code,
      locationType: selectedLocation.type as any,
      rooms: roomsSearch,
      boardType: boardType as any,
    });
  };

  const onLocationChange = (location: LocationSearchSelectedProps | null | undefined, keepOpen?: boolean) => {
    setSelectedLocation(location);
    if (!keepOpen) {
      if (location) {
        dateRef.current?.open();
      }
    }
  };

  const onDatePickerHasValue = () => {
    setTimeout(() => {
      dateRef.current?.close();
      guestRef.current?.open();
    }, 150);
  };

  const onRoomChange = (rooms: RoomSearchInfo[]) => {
    setRooms(rooms);
  };
  const onDatesChange = (dates: any[]) => {
    setDates({
      from: moment(dates[0].format(DATE_ONLY_FORMAT)).toDate(),
      to: dates.length === 2 ? moment(dates[1].format(DATE_ONLY_FORMAT)).toDate() : undefined,
    });
  };
  const onMealTypeChange = mealType => {
    setBoardType(mealType);
  };
  return (
    <div className="button-grid items-center">
      <LocationSearch elmRef={locationRef} onChange={onLocationChange} selectedLocation={selectedLocation} />
      <div className="searchMenu-date px-30 xl:py-20 xl:px-0 js-form-dd js-calendar">
        <div>
          <h4 className="text-15 fw-500 ls-2 lh-16">{t('homePage_SearchCheckInCheckOut')}</h4>
          <DateSearch elmRef={dateRef} from={dates.from} to={dates.to} onDatesChange={onDatesChange} closeAndNext={onDatePickerHasValue} />
        </div>
      </div>
      <GuestSearch elmRef={guestRef} onChange={onRoomChange} rooms={rooms} />
      <MealSearch onChange={onMealTypeChange} boardType={boardType} />
      <div className="button-item">
        <button
          disabled={loading}
          className={`mainSearch__submit button -dark-1 ${height} px-35 col-12 rounded-100 bg-blue-1 text-white`}
          onClick={onSearch}
        >
          {loading ? <FontAwesomeIcon spin icon={faSpinner} className="text-20 mr-10 text-white" /> : <i className="icon-search text-20 mr-10" />}

          {t('homePage_Search')}
        </button>
      </div>
    </div>
  );
}
